import type { ReactNode } from 'react'
import type {
  Artist,
  Flight,
  GuestListItem,
  Hotel,
  ItineraryItem,
  Show,
  Tour,
  TourCrew,
  WithId,
} from '../../types/models'
import { formatDayLong, formatTime } from '../../lib/format'

/* ============================================================================
   The export documents.

   These are real DOM, not a canvas and not a server render. The browser's own
   print engine turns them into the PDF, which means:

     · the type is the app's type, hinted and selectable in the output
     · it works offline, and adds nothing to the bundle
     · page breaks are the browser's problem, and it is good at them

   The Apple apps render the same five documents through SwiftUI's PDF
   renderer. The set and the section order match deliberately — a day sheet
   mailed from the Mac and one mailed from the browser should not be
   distinguishable by the person receiving it.

   Everything here is print-first: `print.css` hides the app around it and
   sets the page box. On screen the same markup is the preview.
   ============================================================================ */

export type Preset = 'show' | 'guestList' | 'travel' | 'itinerary' | 'fullTour'

export const PRESETS: Array<{
  value: Preset
  label: string
  blurb: string
  /** Presets that need a specific show rather than the whole tour. */
  needsShow?: boolean
  /** The plan feature that unlocks it, if any. */
  feature?: 'production_docs' | 'tour_books'
}> = [
  {
    value: 'show',
    label: 'Day sheet',
    blurb: 'One show: running order, venue, contacts and crew.',
    needsShow: true,
  },
  {
    value: 'itinerary',
    label: 'Itinerary',
    blurb: 'Every date and every item, day by day.',
  },
  {
    value: 'travel',
    label: 'Travel',
    blurb: 'Flights and hotels with confirmation numbers.',
    feature: 'production_docs',
  },
  {
    value: 'guestList',
    label: 'Guest list',
    blurb: 'One show, names and counts, for the box office.',
    needsShow: true,
    feature: 'production_docs',
  },
  {
    value: 'fullTour',
    label: 'Tour book',
    blurb: 'The lot, in one document.',
    feature: 'tour_books',
  },
]

export interface DocumentData {
  tour: WithId<Tour>
  artist?: WithId<Artist> | null
  shows: Array<WithId<Show>>
  itinerary: Array<WithId<ItineraryItem>>
  flights: Array<WithId<Flight>>
  hotels: Array<WithId<Hotel>>
  crew: Array<WithId<TourCrew>>
  /** Already scoped to the selected show: guest lists are a subcollection at
   *  `shows/{showId}/guestlist`, so there is no showId to filter on. */
  guests: Array<WithId<GuestListItem>>
}

export interface DocumentOptions {
  preset: Preset
  showId?: string | null
  notes: string
  includeCrew: boolean
  includeNotes: boolean
  /** Free plans carry a footer. Never a diagonal stamp across the page: these
   *  documents go to venues and promoters, so they stay usable. */
  watermarked: boolean
  /** Custom branding replaces the Encore line with the account's own. */
  branding?: { name?: string | null; logoUrl?: string | null } | null
}

/* -------------------------------------------------------------------------- */

export default function ExportDocument({
  data,
  options,
}: {
  data: DocumentData
  options: DocumentOptions
}) {
  const show = options.showId
    ? data.shows.find((entry) => entry.id === options.showId)
    : undefined

  switch (options.preset) {
    case 'show':
      return show ? (
        <DaySheet data={data} options={options} show={show} />
      ) : (
        <Missing what="show" />
      )
    case 'guestList':
      return show ? (
        <GuestList data={data} options={options} show={show} />
      ) : (
        <Missing what="show" />
      )
    case 'travel':
      return <Travel data={data} options={options} />
    case 'itinerary':
      return <Itinerary data={data} options={options} />
    case 'fullTour':
      return <TourBook data={data} options={options} />
  }
}

function Missing({ what }: { what: string }) {
  return <p className="t-caption">Pick a {what} to export.</p>
}

/* ------------------------------------------------------------- day sheet ---- */

function DaySheet({
  data,
  options,
  show,
}: {
  data: DocumentData
  options: DocumentOptions
  show: WithId<Show>
}) {
  const items = data.itinerary
    .filter((item) => item.showId === show.id)
    .sort((a, b) => a.timeUTC.toMillis() - b.timeUTC.toMillis())

  return (
    <Sheet options={options} data={data}>
      <Masthead
        eyebrow={data.tour.artist}
        title={data.tour.tourName}
        right={formatDayLong(show.date, show.timezone)}
      />

      <div className="doc-lead">
        <div>
          <span className="doc-city">{show.city}</span>
          <span className="doc-venue">{show.venueName}</span>
          {show.venueAddress && <span className="doc-sub">{show.venueAddress}</span>}
        </div>
      </div>

      <div className="doc-cols">
        <Block title="Running order">
          <Times show={show} />
        </Block>

        <div className="doc-stack">
          {(show.contactName || show.contactEmail || show.contactPhone) && (
            <Block title="Venue contact">
              <dl className="doc-kv">
                {show.contactName && <Row label="Name" value={show.contactName} />}
                {show.contactEmail && <Row label="Email" value={show.contactEmail} />}
                {show.contactPhone && <Row label="Phone" value={show.contactPhone} />}
              </dl>
            </Block>
          )}

          {options.includeNotes && options.notes.trim() && (
            <Block title="Notes">
              <p className="doc-notes">{options.notes}</p>
            </Block>
          )}
        </div>
      </div>

      {items.length > 0 && (
        <Block title="Schedule">
          <table className="doc-table">
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="doc-time">
                    {item.needsTime ? 'TBC' : formatTime(item.timeUTC, item.timezone)}
                  </td>
                  <td>
                    <strong>{item.title}</strong>
                    {item.subtitle && <div className="doc-sub">{item.subtitle}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Block>
      )}

      {options.includeCrew && data.crew.length > 0 && <CrewTable crew={data.crew} />}
    </Sheet>
  )
}

function Times({ show }: { show: WithId<Show> }) {
  const rows: Array<[string, typeof show.loadIn]> = [
    ['Venue access', show.venueAccess],
    ['Load in', show.loadIn],
    ['Soundcheck', show.soundCheck],
    ['Doors', show.doorsOpen],
    ['Set', show.headlinerSetTime],
    ['Pack out', show.packOut],
  ]
  const set = rows.filter(([, time]) => Boolean(time))
  if (set.length === 0) return <p className="doc-sub">No times set.</p>
  return (
    <dl className="doc-kv">
      {set.map(([label, time]) => (
        <Row key={label} label={label} value={formatTime(time!, show.timezone)} mono />
      ))}
    </dl>
  )
}

/* ------------------------------------------------------------ guest list ---- */

function GuestList({
  data,
  options,
  show,
}: {
  data: DocumentData
  options: DocumentOptions
  show: WithId<Show>
}) {
  const guests = [...data.guests].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
  // `additionalGuests` is a count typed into a text field, so it is a string
  // and can be anything. Anything unparseable counts as nobody extra.
  const total = guests.reduce((sum, guest) => sum + 1 + plusOnes(guest.additionalGuests), 0)

  return (
    <Sheet options={options} data={data}>
      <Masthead
        eyebrow={data.tour.artist}
        title="Guest list"
        right={formatDayLong(show.date, show.timezone)}
      />
      <div className="doc-lead">
        <div>
          <span className="doc-city">{show.city}</span>
          <span className="doc-venue">{show.venueName}</span>
        </div>
        <div className="doc-count">
          <strong>{total}</strong>
          <span>{total === 1 ? 'ticket' : 'tickets'}</span>
        </div>
      </div>

      {guests.length === 0 ? (
        <p className="doc-sub">Nobody on the list.</p>
      ) : (
        <table className="doc-table doc-table-ruled">
          <thead>
            <tr>
              <th>Name</th>
              <th>Note</th>
              <th className="doc-num">Total</th>
              <th className="doc-tick">In</th>
            </tr>
          </thead>
          <tbody>
            {guests.map((guest) => (
              <tr key={guest.id}>
                <td>
                  <strong>{guest.name}</strong>
                </td>
                <td>{guest.note ?? ''}</td>
                <td className="doc-num">{1 + plusOnes(guest.additionalGuests)}</td>
                {/* An empty box, because someone on a door with a pen is the
                    actual last mile of a guest list. */}
                <td className="doc-tick">
                  <span className="doc-box" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Sheet>
  )
}

/* ---------------------------------------------------------------- travel ---- */

function Travel({ data, options }: { data: DocumentData; options: DocumentOptions }) {
  const flights = [...data.flights].sort(
    (a, b) => a.departureTimeUTC.toMillis() - b.departureTimeUTC.toMillis(),
  )
  const hotels = [...data.hotels].sort(
    (a, b) => a.checkInDate.toMillis() - b.checkInDate.toMillis(),
  )

  return (
    <Sheet options={options} data={data}>
      <Masthead eyebrow={data.tour.artist} title="Travel" right={data.tour.tourName} />

      <Block title={`Flights (${flights.length})`}>
        {flights.length === 0 ? (
          <p className="doc-sub">No flights.</p>
        ) : (
          <table className="doc-table doc-table-ruled">
            <thead>
              <tr>
                <th>Date</th>
                <th>Flight</th>
                <th>Route</th>
                <th>Depart</th>
                <th>Arrive</th>
                <th>Ref</th>
              </tr>
            </thead>
            <tbody>
              {flights.map((flight) => (
                <tr key={flight.id}>
                  <td className="doc-time">{formatDayLong(flight.departureTimeUTC)}</td>
                  <td>
                    <strong>
                      {flight.airline ?? ''} {flight.flightNumber ?? ''}
                    </strong>
                  </td>
                  <td>
                    {flight.origin} &rarr; {flight.destination}
                  </td>
                  <td className="doc-time">{formatTime(flight.departureTimeUTC)}</td>
                  <td className="doc-time">{formatTime(flight.arrivalTimeUTC)}</td>
                  <td className="doc-time">{flight.bookingReference ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Block>

      <Block title={`Hotels (${hotels.length})`}>
        {hotels.length === 0 ? (
          <p className="doc-sub">No hotels.</p>
        ) : (
          <table className="doc-table doc-table-ruled">
            <thead>
              <tr>
                <th>Hotel</th>
                <th>City</th>
                <th>In</th>
                <th>Out</th>
                <th>Ref</th>
              </tr>
            </thead>
            <tbody>
              {hotels.map((hotel) => (
                <tr key={hotel.id}>
                  <td>
                    <strong>{hotel.name}</strong>
                    {hotel.address && <div className="doc-sub">{hotel.address}</div>}
                  </td>
                  <td>{hotel.city}</td>
                  <td className="doc-time">{formatDayLong(hotel.checkInDate, hotel.timezone)}</td>
                  <td className="doc-time">{formatDayLong(hotel.checkOutDate, hotel.timezone)}</td>
                  <td className="doc-time">{hotel.bookingReference ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Block>
    </Sheet>
  )
}

/* ------------------------------------------------------------- itinerary ---- */

function Itinerary({ data, options }: { data: DocumentData; options: DocumentOptions }) {
  const days = groupByDay(data.itinerary)

  return (
    <Sheet options={options} data={data}>
      <Masthead eyebrow={data.tour.artist} title={data.tour.tourName} right="Itinerary" />

      {days.length === 0 ? (
        <p className="doc-sub">Nothing scheduled.</p>
      ) : (
        days.map(({ key, label, items }) => (
          <div key={key} className="doc-day">
            <h3 className="doc-day-head">{label}</h3>
            <table className="doc-table">
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td className="doc-time">
                      {item.needsTime ? 'TBC' : formatTime(item.timeUTC, item.timezone)}
                    </td>
                    <td>
                      <strong>{item.title}</strong>
                      {item.subtitle && <div className="doc-sub">{item.subtitle}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </Sheet>
  )
}

/* ------------------------------------------------------------- tour book ---- */

function TourBook({ data, options }: { data: DocumentData; options: DocumentOptions }) {
  const shows = [...data.shows].sort((a, b) => a.date.toMillis() - b.date.toMillis())

  return (
    <Sheet options={options} data={data}>
      {/* Cover. Its own page, always. */}
      <section className="doc-cover">
        <span className="doc-cover-eyebrow">{data.tour.artist}</span>
        <h1 className="doc-cover-title">{data.tour.tourName}</h1>
        <span className="doc-cover-dates">
          {formatDayLong(data.tour.startDate)} &ndash; {formatDayLong(data.tour.endDate)}
        </span>
      </section>

      <div className="doc-page-break" />

      <Masthead eyebrow={data.tour.artist} title="Shows" right={`${shows.length} dates`} />
      <table className="doc-table doc-table-ruled">
        <thead>
          <tr>
            <th>Date</th>
            <th>City</th>
            <th>Venue</th>
            <th>Set</th>
          </tr>
        </thead>
        <tbody>
          {shows.map((show) => (
            <tr key={show.id}>
              <td className="doc-time">{formatDayLong(show.date, show.timezone)}</td>
              <td>
                <strong>{show.city}</strong>
              </td>
              <td>{show.venueName}</td>
              <td className="doc-time">
                {show.headlinerSetTime ? formatTime(show.headlinerSetTime, show.timezone) : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="doc-page-break" />
      <Itinerary data={data} options={{ ...options, preset: 'itinerary' }} />

      <div className="doc-page-break" />
      <Travel data={data} options={{ ...options, preset: 'travel' }} />

      {options.includeCrew && data.crew.length > 0 && (
        <>
          <div className="doc-page-break" />
          <Masthead eyebrow={data.tour.artist} title="Crew" right={`${data.crew.length} people`} />
          <CrewTable crew={data.crew} />
        </>
      )}
    </Sheet>
  )
}

/* ------------------------------------------------------------------ bits ---- */

function Sheet({
  children,
  options,
  data,
}: {
  children: ReactNode
  options: DocumentOptions
  data: DocumentData
}) {
  return (
    <article className="doc">
      {children}
      <Footer options={options} data={data} />
    </article>
  )
}

/**
 * One footer for every document.
 *
 * Mirrors `Export/ExportFooter.swift`: what it renders is a plan question, not
 * a per-document decision. A free account gets a small line, a custom-branding
 * account gets its own name, and white-label gets nothing at all.
 */
function Footer({ options, data }: { options: DocumentOptions; data: DocumentData }) {
  const custom = options.branding?.name?.trim()

  if (options.watermarked) {
    return (
      <footer className="doc-footer">
        <span>{data.tour.tourName}</span>
        <span className="doc-footer-mark">Made with Encore · encore-app.com</span>
      </footer>
    )
  }

  return (
    <footer className="doc-footer">
      <span>{data.tour.tourName}</span>
      {custom && <span>{custom}</span>}
    </footer>
  )
}

function Masthead({
  eyebrow,
  title,
  right,
}: {
  eyebrow: string
  title: string
  right?: string
}) {
  return (
    <header className="doc-masthead">
      <div>
        <span className="doc-eyebrow">{eyebrow}</span>
        <h2 className="doc-title">{title}</h2>
      </div>
      {right && <span className="doc-right">{right}</span>}
    </header>
  )
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="doc-block">
      <h3 className="doc-block-head">{title}</h3>
      {children}
    </section>
  )
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <>
      <dt>{label}</dt>
      <dd className={mono ? 'doc-time' : undefined}>{value}</dd>
    </>
  )
}

function CrewTable({ crew }: { crew: Array<WithId<TourCrew>> }) {
  return (
    <Block title={`Crew (${crew.length})`}>
      <table className="doc-table doc-table-ruled">
        <thead>
          <tr>
            <th>Name</th>
            <th>Role</th>
            <th>Contact</th>
          </tr>
        </thead>
        <tbody>
          {crew.map((member) => (
            <tr key={member.id}>
              <td>
                <strong>{member.name}</strong>
              </td>
              <td>{(member.roles ?? []).join(', ')}</td>
              <td>
                {member.email && <div>{member.email}</div>}
                {member.phone && <div>{member.phone}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Block>
  )
}

/** Groups itinerary items into calendar days, in order. */
function groupByDay(items: Array<WithId<ItineraryItem>>) {
  const map = new Map<string, { key: string; label: string; items: Array<WithId<ItineraryItem>> }>()
  ;[...items]
    .sort((a, b) => a.timeUTC.toMillis() - b.timeUTC.toMillis())
    .forEach((item) => {
      const date = item.timeUTC.toDate()
      const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
      const existing = map.get(key)
      if (existing) existing.items.push(item)
      else
        map.set(key, {
          key,
          label: formatDayLong(item.timeUTC, item.timezone),
          items: [item],
        })
    })
  return [...map.values()]
}

/** `additionalGuests` is free text. Anything that isn't a number is nobody. */
function plusOnes(value: string | undefined): number {
  const parsed = Number.parseInt((value ?? '').trim(), 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
}
